(() => {
  const fr = (document.documentElement.lang || 'fr').toLowerCase().startsWith('fr');
  const formatMoney = (cents) => {
    const value = Number(cents || 0);
    if (value <= 0) return '';
    if (window.FrostPeakTheme?.formatMoney && window.FrostPeak?.moneyFormat) {
      return window.FrostPeakTheme.formatMoney(value, window.FrostPeak.moneyFormat);
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'fr-CA', {
      style:'currency',
      currency:window.Shopify?.currency?.active || 'CAD'
    }).format(value / 100);
  };

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      try { this.variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]'); }
      catch (_) { this.variants = []; }

      this.variantSelect = this.querySelector('[data-variant-select]');
      this.price = this.querySelector('[data-product-price]');
      this.compare = this.querySelector('[data-product-compare-price]');
      this.sku = this.querySelector('[data-product-sku]');
      this.skuRow = this.querySelector('[data-product-sku-row]');
      this.stock = this.querySelector('[data-product-stock]');
      this.addButton = this.querySelector('[data-add-to-cart]');
      this.addText = this.querySelector('[data-add-text]');
      this.backInStock = this.querySelector('[data-back-in-stock]');
      this.restockVariant = this.querySelector('[data-restock-variant]');
      this.restockMessage = this.querySelector('[data-restock-message]');
      this.optionInputs = [...this.querySelectorAll('[data-option-value]')];
      this.isPreorder = this.dataset.preorder === 'true';

      this.optionInputs.forEach((input) => input.addEventListener('change', () => this.onOptionChange()));
      if (this.variantSelect?.tagName === 'SELECT') this.variantSelect.addEventListener('change', () => this.onSelectChange());

      const initialId = Number(this.variantSelect?.value || this.dataset.currentVariant || 0);
      const initial =
        this.variants.find((v) => Number(v.id) === initialId) ||
        this.variants.find((v) => v.available && Number(v.price) > 0) ||
        this.variants.find((v) => Number(v.price) > 0) ||
        this.variants[0];

      if (initial) {
        this.syncOptionInputs(initial);
        this.updateVariant(initial, false);
      }
    }

    selectedOptions() {
      return [...this.querySelectorAll('[data-option-group]')]
        .sort((a,b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((group) => group.querySelector('[data-option-value]:checked')?.value || '');
    }

    findVariant(options) {
      return this.variants.find((variant) =>
        variant.options.every((option,index) => String(option) === String(options[index]))
      );
    }

    onOptionChange() {
      const variant = this.findVariant(this.selectedOptions());
      if (!variant) return this.setUnavailable();
      this.updateVariant(variant, true);
    }

    onSelectChange() {
      const variant = this.variants.find((item) => Number(item.id) === Number(this.variantSelect.value));
      if (!variant) return;
      this.syncOptionInputs(variant);
      this.updateVariant(variant, true);
    }

    syncOptionInputs(variant) {
      variant.options.forEach((value,index) => {
        const input = this.optionInputs.find((item) =>
          Number(item.dataset.optionPosition) === index + 1 && String(item.value) === String(value)
        );
        if (input) input.checked = true;
      });
    }

    updateVariant(variant, updateUrl = true) {
      this.dataset.currentVariant = variant.id;
      if (this.variantSelect) this.variantSelect.value = String(variant.id);

      const formatted = formatMoney(variant.price);
      if (this.price) {
        this.price.textContent = formatted;
        this.price.hidden = !formatted;
      }

      if (this.compare) {
        const hasCompare = Number(variant.compare_at_price || 0) > Number(variant.price || 0) && Number(variant.price || 0) > 0;
        this.compare.hidden = !hasCompare;
        this.compare.textContent = hasCompare ? formatMoney(variant.compare_at_price) : '';
      }

      if (this.sku) this.sku.textContent = variant.sku || '';
      if (this.skuRow) this.skuRow.hidden = !variant.sku;

      const canBuy = Boolean(variant.available) && Number(variant.price) > 0;
      if (this.stock) {
        this.stock.textContent = this.isPreorder ? (fr ? 'Précommande' : 'Preorder') : (variant.available ? (fr ? 'En stock' : 'In stock') : (fr ? 'Épuisé' : 'Sold out'));
        this.stock.classList.toggle('is-preorder', this.isPreorder);
        this.stock.classList.toggle('is-out', !variant.available);
      }
      if (this.addButton) this.addButton.disabled = !canBuy;
      if (this.addText && this.addButton) {
        this.addText.textContent = canBuy ? this.addButton.dataset.availableText : this.addButton.dataset.soldText;
      }

      if (this.backInStock) this.backInStock.hidden = canBuy;
      if (this.restockVariant) this.restockVariant.textContent = (variant.options || []).join(' / ');
      if (this.restockMessage) {
        const title = this.querySelector('h1')?.textContent?.trim() || '';
        this.restockMessage.value = `Back-in-stock request — ${title} — ${(variant.options || []).join(' / ')} — SKU ${variant.sku || ''}`;
      }

      if (variant.featured_media_id) this.showMedia(variant.featured_media_id);
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant',variant.id);
        history.replaceState({},'',url);
      }
    }

    setUnavailable() {
      if (this.addButton) this.addButton.disabled = true;
      if (this.addText && this.addButton) this.addText.textContent = this.addButton.dataset.unavailableText;
      if (this.stock) {
        this.stock.textContent = fr ? 'Indisponible' : 'Unavailable';
        this.stock.classList.add('is-out');
      }
    }

    showMedia(id) {
      const page = this.closest('[data-product-page]');
      if (!page) return;
      page.querySelectorAll('[data-product-media]').forEach((media) => {
        const active = String(media.dataset.productMedia) === String(id);
        media.classList.toggle('is-active',active);
        media.hidden = !active;
      });
      page.querySelectorAll('[data-media-thumbnail]').forEach((thumb) => {
        const active = String(thumb.dataset.mediaThumbnail) === String(id);
        thumb.classList.toggle('is-active',active);
        thumb.setAttribute('aria-pressed',String(active));
      });
    }
  }

  if (!customElements.get('product-info')) customElements.define('product-info',ProductInfo);

  document.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-media-thumbnail]');
    if (thumb) {
      const page = thumb.closest('[data-product-page]');
      const id = thumb.dataset.mediaThumbnail;
      page?.querySelectorAll('[data-product-media]').forEach((media) => {
        const active = String(media.dataset.productMedia) === String(id);
        media.classList.toggle('is-active',active);
        media.hidden = !active;
      });
      page?.querySelectorAll('[data-media-thumbnail]').forEach((item) => {
        const active = item === thumb;
        item.classList.toggle('is-active',active);
        item.setAttribute('aria-pressed',String(active));
      });
      return;
    }

    const zoom = event.target.closest('[data-media-zoom]');
    if (zoom) {
      const page = zoom.closest('[data-product-page]');
      const modal = page?.querySelector('[data-media-modal]');
      const image = modal?.querySelector('[data-media-modal-image]');
      if (!modal || !image) return;
      image.src = zoom.dataset.mediaSrc || '';
      image.alt = zoom.dataset.mediaAlt || '';
      if (typeof modal.showModal === 'function') modal.showModal(); else modal.setAttribute('open','');
      return;
    }

    const close = event.target.closest('[data-media-modal-close]');
    if (close) {
      const modal = close.closest('[data-media-modal]');
      if (typeof modal?.close === 'function') modal.close(); else modal?.removeAttribute('open');
    }
  });
})();
